import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle } from 'lucide-react';
import { listLscNeedsAttention } from '../../api';
import { lscPath } from '../../routing';

export function LiveReviewNeededBannerView({
  leagueId,
  count,
}: {
  leagueId: string;
  count: number;
}) {
  if (count <= 0) return null;
  const message =
    count === 1
      ? 'A live game was detected that needs your review before it can appear as verified.'
      : `${count} live games were detected that need your review before they can appear as verified.`;
  return (
    <div
      data-testid="live-review-needed-banner"
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-amber-100">Live game needs review</p>
          <p className="text-sm text-amber-100/90" data-testid="live-review-needed-message">
            {message}
          </p>
        </div>
      </div>
      <Link
        to={lscPath(`/leagues/${leagueId}/needs-attention`)}
        className="btn-primary text-sm shrink-0"
        data-testid="live-review-needed-cta"
      >
        Review now
      </Link>
    </div>
  );
}

export type LiveReviewNeededBannerProps = {
  leagueId: string;
  isCommissioner: boolean;
};

// Commissioner-only Home CTA. When live detection finds a likely league game
// that is not yet promotion-eligible (routed to Needs Attention), surface a
// clear call to action instead of letting the game silently disappear. Members
// never see this — the underlying items are commissioner-gated server-side.
const LiveReviewNeededBanner: React.FC<LiveReviewNeededBannerProps> = ({
  leagueId,
  isCommissioner,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isCommissioner) {
      setCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const items = await listLscNeedsAttention(leagueId);
        if (cancelled) return;
        const liveReviewCount = items.filter(
          (item) => item.is_live_exception && item.state !== 'commissioner_approved'
        ).length;
        setCount(liveReviewCount);
      } catch {
        // The Home CTA must never break the page; stay silent on error.
        if (!cancelled) setCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, isCommissioner]);

  if (!isCommissioner) return null;
  return <LiveReviewNeededBannerView leagueId={leagueId} count={count} />;
};

export default LiveReviewNeededBanner;
